import type { EngineDocumentResponseExpanded } from '@engine/models/document'

import { ActivityTimeline } from '#components/ActivityTimeline'
import { ExpandableJsonPanel } from '#components/ExpandableJsonPanel.js'
import { KeyValueDisplay, KeyValueItem } from '#components/KeyValueDisplay'

import { DocumentStatusBadge } from './DocumentStatusBadge.js'

interface DocumentDetailsProps {
  document?: EngineDocumentResponseExpanded | null
}

const DocumentDetails = ({ document }: DocumentDetailsProps) => {
  if (!document) return null

  const items: KeyValueItem[] = [
    { label: 'Document ID', value: document._id },
    ...(document.externalId
      ? [{ label: 'External ID', value: document.externalId }]
      : []),
    ...(document.trackingNumber
      ? [{ label: 'Tracking Number', value: document.trackingNumber }]
      : []),
    ...(document.requestId
      ? [{ label: 'Request ID', value: document.requestId }]
      : []),
    ...(document.pickupDate
      ? [{ label: 'Pickup Date', value: document.pickupDate }]
      : []),
    ...(document.tenantId
      ? [{ label: 'Tenant ID', value: document.tenantId }]
      : []),
    ...(document.carrierConnectionId
      ? [
          {
            label: 'Carrier Connection ID',
            value: document.carrierConnectionId,
          },
        ]
      : []),
    ...(document.carrierCode
      ? [
          {
            label: 'Carrier Code',
            value: document.carrierCode.toUpperCase(),
            className: 'uppercase',
          },
        ]
      : []),
    {
      label: 'Status',
      value: <DocumentStatusBadge status={document.status} />,
      className: 'mt-1',
    },
    ...(document.documents?.length
      ? [
          {
            label: 'Documents',
            value: document.documents
              .map(d => `${d.fileName} (${d.type})`)
              .join(', '),
          },
        ]
      : []),
    ...(document.lastUpdated
      ? [
          {
            label: 'Last Updated',
            value: new Date(document.lastUpdated).toLocaleString(),
          },
        ]
      : []),
    ...(document.createdAt
      ? [
          {
            label: 'Created At',
            value: new Date(document.createdAt).toLocaleString(),
          },
        ]
      : []),
    {
      label: 'Next Run',
      value: document.nextRunDate
        ? new Date(document.nextRunDate).toLocaleString()
        : 'Not queued',
    },
  ]

  return (
    <div>
      <KeyValueDisplay items={items} className="col-span-full" />
      <div className="col-span-full mt-6">
        <ExpandableJsonPanel
          title="Full Document Data"
          data={document}
          className="col-span-full mb-6"
          preClassName="bg-slate-950/80 border border-base-300 font-mono text-base-content"
        />
        <ActivityTimeline
          externalId={document.externalId}
          emptyMessage="No traces or request logs found for this document."
        />
      </div>
    </div>
  )
}

export { DocumentDetails }
